/**
 * Grant Service
 *
 * Handles all grant-related API operations
 */

import { API, graphqlOperation } from 'aws-amplify';
import {
  listGrants,
  getGrant,
  permGetApplicableGrants
} from '../../../../../graphql/queries';
import {
  createGrant as createGrantMutation,
  updateGrant as updateGrantMutation,
  deleteGrant as deleteGrantMutation
} from '../../../../../graphql/mutations';

// Custom query to handle v5.002 grants safely
// Only fetches grants where actionConfigs is not null
const listGrantsV5 = /* GraphQL */ `
  query ListGrantsV5(
    $filter: ModelGrantFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listGrants(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        clientId
        name
        description
        targetDomain
        targetResourceTypeId
        targetResourceId
        targetResourcePattern
        subjectType
        subjectValue
        actionConfigs {
          action
          effect
          scopeConfig {
            includeChildren
            includePeers
            includeParent
            __typename
          }
          constraints
          priority
          __typename
        }
        priority
        constraints
        effectiveFrom
        effectiveTo
        isActive
        createdAt
        updatedAt
        createdBy
        updatedBy
        grantSignature
        __typename
      }
      nextToken
      __typename
    }
  }
`;

// Custom Lambda resolvers
const LAMBDA_ENDPOINTS = {
  VALIDATE: 'permValidateGrantQuery',
  BATCH_CREATE: 'permBatchCreateGrants',
  MUTATION_WRAPPER: 'permMutationWrappers',
  GET_APPLICABLE: 'permGetApplicableGrants'
};

class GrantService {
  /**
   * List grants
   * @param {string|null} clientId - Client ID to filter by, or null to fetch all grants (APP_ADMIN)
   * @param {object} filters - Additional filters
   */
  async listGrants(clientId, filters = {}) {
    try {
      // Build filter: only include clientId filter if provided (CLIENT_ADMIN)
      // If clientId is null, fetch all grants from all clients (APP_ADMIN)
      const filter = clientId
        ? { clientId: { eq: clientId }, ...filters }
        : { ...filters };

      console.log('listGrants - Input:', { clientId, filters });
      console.log('listGrants - Filter:', filter);
      console.log('listGrants - Query:', listGrantsV5);

      const response = await API.graphql(
        graphqlOperation(listGrantsV5, { filter, limit: 1000 })
      );

      // Filter out null items and legacy grants (v5.002 migration)
      // Legacy grants have null actionConfigs and cannot be displayed in v5.002 UI
      let grants = (response.data.listGrants.items || [])
        .filter(item => item !== null && item.actionConfigs !== null);

      let nextToken = response.data.listGrants.nextToken;

      // Fetch all pages
      while (nextToken) {
        const nextResponse = await API.graphql(
          graphqlOperation(listGrants, { filter, limit: 1000, nextToken })
        );
        // Filter out null items and legacy grants from each page
        const validItems = (nextResponse.data.listGrants.items || [])
          .filter(item => item !== null && item.actionConfigs !== null);
        grants = [...grants, ...validItems];
        nextToken = nextResponse.data.listGrants.nextToken;
      }

      console.log(`Loaded ${grants.length} v5.002 grants (filtered out legacy grants)`);

      // Add status field based on isActive for UI consistency
      grants = grants.map(grant => ({
        ...grant,
        status: grant.isActive ? 'ACTIVE' : 'INACTIVE'
      }));

      return grants;
    } catch (error) {
      console.error('Error listing grants:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));

      // Check if the error is due to missing required fields (v5.002 migration)
      const errorMessage = error.message || '';
      const hasGraphQLErrors = error.errors && error.errors.length > 0;
      const errorMessages = hasGraphQLErrors ? error.errors.map(e => e.message).join(' ') : '';

      if (errorMessage.includes('non-nullable') ||
          errorMessage.includes('actionConfigs') ||
          errorMessage.includes('grantSignature') ||
          errorMessages.includes('non-nullable') ||
          errorMessages.includes('actionConfigs') ||
          errorMessages.includes('null')) {
        console.warn('Legacy grants detected with null actionConfigs. These cannot be loaded in v5.002 UI.');
        console.warn('Please delete or migrate legacy grants to fix this issue.');
        console.warn('You can identify legacy grants by querying for grants where actionConfigs IS NULL');
        // Return empty array to allow the UI to load
        return [];
      }
      throw new Error(`Failed to list grants: ${error.message}`);
    }
  }

  /**
   * Get a single grant by ID
   */
  async getGrant(grantId) {
    try {
      const response = await API.graphql(
        graphqlOperation(getGrant, { id: grantId })
      );
      const grant = response.data.getGrant;
      if (grant) {
        grant.status = grant.isActive ? 'ACTIVE' : 'INACTIVE';
      }
      return grant;
    } catch (error) {
      console.error('Error getting grant:', error);
      throw new Error(`Failed to get grant: ${error.message}`);
    }
  }

  /**
   * Create a new grant using the permMutationWrappers Lambda
   * This Lambda handles grantSignature generation, validation, and permission recalculation
   */
  async createGrant(grantData, clientId) {
    try {
      // Map status to isActive: ACTIVE -> true, anything else -> false
      let isActive = true;
      if (grantData.status !== undefined) {
        isActive = grantData.status === 'ACTIVE';
      } else if (grantData.isActive !== undefined) {
        isActive = grantData.isActive;
      }

      const resolvedClientId = grantData.clientId || clientId;

      // Prepare the grant data for the Lambda function
      const grant = {
        clientId: resolvedClientId,
        // v5.002 structured fields
        name: grantData.name,
        description: grantData.description,
        targetDomain: grantData.targetDomain,
        targetResourceTypeId: grantData.targetResourceTypeId,
        targetResourceId: grantData.targetResourceId,
        // v5.002: actionConfigs array (required)
        actionConfigs: grantData.actionConfigs,
        // Legacy fields (for backward compatibility, but actionConfigs takes precedence)
        action: grantData.action,
        effect: grantData.effect,
        scopeConfig: grantData.scopeConfig,
        // Common fields
        subjectType: grantData.subjectType,
        subjectValue: grantData.subjectValue,
        priority: grantData.priority || 100,
        isActive: isActive,
        effectiveFrom: grantData.effectiveFrom,
        effectiveTo: grantData.effectiveTo,
        constraints: grantData.constraints,
        createdBy: grantData.createdBy
      };

      // Clean up actionConfigs - remove null priority and constraints
      if (grant.actionConfigs && Array.isArray(grant.actionConfigs)) {
        grant.actionConfigs = grant.actionConfigs.map(actionConfig => {
          const cleaned = { ...actionConfig };
          // Remove null or undefined priority (let it use grant-level priority)
          if (cleaned.priority === null || cleaned.priority === undefined) {
            delete cleaned.priority;
          }
          // Remove null or undefined constraints
          if (cleaned.constraints === null || cleaned.constraints === undefined) {
            delete cleaned.constraints;
          }
          return cleaned;
        });
      }

      console.log('Grant data being sent to Lambda:', grant);

      // Remove undefined values (nulls are kept as they represent wildcards for target fields)
      Object.keys(grant).forEach(key => {
        if (grant[key] === undefined) {
          delete grant[key];
        }
      });

      // Invoke the permMutationWrappers Lambda directly
      const response = await API.graphql({
        query: `
          query InvokePermMutationWrapper($operation: String!, $grant: AWSJSON!, $clientId: String!) {
            invokePermMutationWrappers(operation: $operation, grant: $grant, clientId: $clientId)
          }
        `,
        variables: {
          operation: 'CREATE',
          grant: JSON.stringify(grant),
          clientId: resolvedClientId
        }
      });

      console.log('Lambda response:', response.data.invokePermMutationWrappers);

      // Check if Lambda returned null (indicates Lambda invocation failure)
      if (!response.data.invokePermMutationWrappers) {
        throw new Error(
          'Lambda function returned null. This usually indicates:\n' +
          '1. Lambda function not deployed to this environment\n' +
          '2. AppSync permissions issue preventing Lambda invocation\n' +
          '3. Lambda function error before returning response\n' +
          'Check CloudWatch logs for permMutationWrappers Lambda and AppSync API logs.'
        );
      }

      const result = JSON.parse(response.data.invokePermMutationWrappers);

      if (!result.success) {
        // Log validation errors if present
        if (result.validationErrors) {
          console.error('Validation errors:', result.validationErrors);
        }
        if (result.validationWarnings) {
          console.warn('Validation warnings:', result.validationWarnings);
        }

        // Build detailed error message
        let errorMessage = result.error || 'Grant validation failed';
        if (result.validationErrors && result.validationErrors.length > 0) {
          const errorDetails = result.validationErrors.map(e => e.message).join(', ');
          errorMessage = `${errorMessage}: ${errorDetails}`;
        }

        throw new Error(errorMessage);
      }

      // Add the status field back to the response for UI consistency
      if (result.grant) {
        result.grant.status = result.grant.isActive ? 'ACTIVE' : 'INACTIVE';
      }

      return result.grant;
    } catch (error) {
      console.error('Error creating grant:', error);
      throw error;
    }
  }

  /**
   * Update an existing grant using the permMutationWrappers Lambda
   * This Lambda handles grantSignature regeneration, validation, and permission recalculation
   */
  async updateGrant(grantId, updates, clientId) {
    try {
      // Map status to isActive if status is provided
      let isActive = updates.isActive;
      if (updates.status !== undefined) {
        isActive = updates.status === 'ACTIVE';
      }

      const resolvedClientId = updates.clientId || clientId;

      // Prepare the grant updates for the Lambda function
      const grant = {
        id: grantId,
        clientId: resolvedClientId,
        // v5 structured fields
        name: updates.name,
        description: updates.description,
        targetDomain: updates.targetDomain,
        targetResourceTypeId: updates.targetResourceTypeId,
        targetResourceId: updates.targetResourceId,
        action: updates.action,
        // Common fields
        subjectType: updates.subjectType,
        subjectValue: updates.subjectValue,
        effect: updates.effect,
        priority: updates.priority,
        isActive: isActive,
        effectiveFrom: updates.effectiveFrom,
        effectiveTo: updates.effectiveTo,
        scopeConfig: updates.scopeConfig,
        constraints: updates.constraints,
        createdBy: updates.createdBy
      };

      // Remove undefined values (nulls are kept as they represent wildcards)
      Object.keys(grant).forEach(key => {
        if (grant[key] === undefined) {
          delete grant[key];
        }
      });

      // Invoke the permMutationWrappers Lambda directly
      const response = await API.graphql({
        query: `
          query InvokePermMutationWrapper($operation: String!, $grantId: String!, $grant: AWSJSON!, $clientId: String!) {
            invokePermMutationWrappers(operation: $operation, grantId: $grantId, grant: $grant, clientId: $clientId)
          }
        `,
        variables: {
          operation: 'UPDATE',
          grantId: grantId,
          grant: JSON.stringify(grant),
          clientId: resolvedClientId
        }
      });

      // Check if Lambda returned null (indicates Lambda invocation failure)
      if (!response.data.invokePermMutationWrappers) {
        throw new Error(
          'Lambda function returned null. This usually indicates:\n' +
          '1. Lambda function not deployed to this environment\n' +
          '2. AppSync permissions issue preventing Lambda invocation\n' +
          '3. Lambda function error before returning response\n' +
          'Check CloudWatch logs for permMutationWrappers Lambda and AppSync API logs.'
        );
      }

      const result = JSON.parse(response.data.invokePermMutationWrappers);

      if (!result.success) {
        throw new Error(result.error || 'Failed to update grant');
      }

      // Add the status field back to the response for UI consistency
      if (result.grant) {
        result.grant.status = result.grant.isActive ? 'ACTIVE' : 'INACTIVE';
      }

      return result.grant;
    } catch (error) {
      console.error('Error updating grant:', error);
      throw error;
    }
  }

  /**
   * Delete a grant using the permMutationWrappers Lambda
   * This Lambda handles permission recalculation after deletion
   */
  async deleteGrant(grantId, clientId) {
    try {
      // Invoke the permMutationWrappers Lambda directly
      const response = await API.graphql({
        query: `
          query InvokePermMutationWrapper($operation: String!, $grantId: String!, $clientId: String!) {
            invokePermMutationWrappers(operation: $operation, grantId: $grantId, clientId: $clientId)
          }
        `,
        variables: {
          operation: 'DELETE',
          grantId: grantId,
          clientId: clientId
        }
      });

      // Check if Lambda returned null (indicates Lambda invocation failure)
      if (!response.data.invokePermMutationWrappers) {
        throw new Error(
          'Lambda function returned null. This usually indicates:\n' +
          '1. Lambda function not deployed to this environment\n' +
          '2. AppSync permissions issue preventing Lambda invocation\n' +
          '3. Lambda function error before returning response\n' +
          'Check CloudWatch logs for permMutationWrappers Lambda and AppSync API logs.'
        );
      }

      const result = JSON.parse(response.data.invokePermMutationWrappers);

      if (!result.success) {
        throw new Error(result.error || 'Failed to delete grant');
      }

      return result.deletedGrant;
    } catch (error) {
      console.error('Error deleting grant:', error);
      throw error;
    }
  }

  /**
   * Validate a grant configuration
   */
  async validateGrant(grant, mode, clientId) {
    try {
      console.log('validateGrant called with:', { grant, mode, clientId });

      // Determine clientId - try multiple sources
      const resolvedClientId = clientId || grant.clientId || 'rdx';
      console.log('Resolved clientId:', resolvedClientId);

      // Transform grant to match CreateGrantInput schema
      // Only include fields that are defined in CreateGrantInput
      const grantInput = {
        clientId: resolvedClientId,
        targetGroup: grant.targetGroup,
        targetGroupRole: grant.targetGroupRole,
        targetOrgLevel: grant.targetOrgLevel,
        targetResourcePattern: grant.targetResourcePattern,
        subjectType: grant.subjectType,
        subjectValue: grant.subjectValue,
        effect: grant.effect,
        priority: grant.priority,
        effectiveFrom: grant.effectiveFrom,
        effectiveTo: grant.effectiveTo,
        scopeConfig: grant.scopeConfig ? JSON.stringify(grant.scopeConfig) : null,
        constraints: grant.constraints ? JSON.stringify(grant.constraints) : null,
        isActive: grant.isActive,
        createdBy: grant.createdBy
      };

      console.log('Transformed grantInput:', grantInput);

      const response = await API.graphql({
        query: `
          query ValidateGrant($grant: CreateGrantInput!) {
            permValidateGrant(grant: $grant) {
              isValid
              errors {
                field
                code
                message
                suggestion
              }
              warnings {
                field
                code
                message
                suggestion
              }
              summary {
                hasErrors
                hasWarnings
                errorCount
                warningCount
              }
              canProceed
              requiresConfirmation
            }
          }
        `,
        variables: {
          grant: grantInput
        }
      });

      return response.data.permValidateGrant;
    } catch (error) {
      console.error('Error validating grant:', error);
      throw new Error(`Failed to validate grant: ${error.message}`);
    }
  }

  /**
   * Create multiple grants in batch
   */
  async batchCreateGrants(grants, clientId) {
    try {
      const response = await API.graphql({
        query: `
          query BatchCreateGrants($grants: [GrantInput]!, $clientId: String!) {
            invokePermBatchCreateGrants(grants: $grants, clientId: $clientId)
          }
        `,
        variables: {
          grants,
          clientId
        }
      });

      const result = JSON.parse(response.data.invokePermBatchCreateGrants);

      if (!result.success) {
        throw new Error(result.error || 'Batch creation failed');
      }

      return result;
    } catch (error) {
      console.error('Error batch creating grants:', error);
      throw new Error(`Failed to batch create grants: ${error.message}`);
    }
  }

  /**
   * Delete multiple grants
   */
  async batchDeleteGrants(grantIds, clientId) {
    try {
      const results = await Promise.all(
        grantIds.map(id => this.deleteGrant(id, clientId).catch(err => ({
          id,
          success: false,
          error: err.message
        })))
      );

      const succeeded = results.filter(r => r.success !== false).length;
      const failed = results.filter(r => r.success === false);

      return {
        deleted: succeeded,
        failed: failed.length,
        errors: failed
      };
    } catch (error) {
      console.error('Error batch deleting grants:', error);
      throw new Error(`Failed to batch delete grants: ${error.message}`);
    }
  }

  /**
   * Get applicable grants for a user
   */
  async getApplicableGrants(userId, clientId, options = {}) {
    try {
      console.log('[grantService] Getting applicable grants for:', { userId, clientId, options });

      const response = await API.graphql(
        graphqlOperation(permGetApplicableGrants, {
          userId: userId,
          clientId: clientId,
          includeInactive: options.includeInactive || false,
          includeExpired: options.includeExpired || false,
          resourceFilter: options.resourceFilter || null
        })
      );

      console.log('[grantService] permGetApplicableGrants response:', response.data);

      const rawResult = response.data.permGetApplicableGrants;

      console.log('[grantService] Raw grants field:', {
        grants: rawResult?.grants,
        type: typeof rawResult?.grants,
        isArray: Array.isArray(rawResult?.grants),
        length: rawResult?.grants?.length
      });

      // Parse grants - handle both string and array formats
      // Note: grants might be double-encoded by GraphQL/AppSync
      let parsedGrants = [];

      if (rawResult?.grants) {
        if (typeof rawResult.grants === 'string') {
          try {
            console.log('[grantService] Parsing grants string (first parse)');
            let firstParse = JSON.parse(rawResult.grants);

            // Check if we need to parse again (double-encoded)
            if (typeof firstParse === 'string') {
              console.log('[grantService] Grants is double-encoded, parsing again');
              parsedGrants = JSON.parse(firstParse);
            } else {
              parsedGrants = firstParse;
            }

            console.log('[grantService] After parsing:', {
              isArray: Array.isArray(parsedGrants),
              length: parsedGrants?.length,
              constructor: parsedGrants?.constructor?.name
            });
          } catch (parseError) {
            console.error('[grantService] Error parsing grants JSON:', parseError);
            parsedGrants = [];
          }
        } else if (Array.isArray(rawResult.grants)) {
          console.log('[grantService] Grants already an array');
          parsedGrants = rawResult.grants;
        }
      }

      // Ensure it's an array
      if (!parsedGrants || !Array.isArray(parsedGrants)) {
        console.warn('[grantService] parsedGrants is not an array, setting to empty array. Type:', typeof parsedGrants);
        parsedGrants = [];
      }

      console.log('[grantService] Parsed grants count:', parsedGrants.length);

      // Remove GraphQL __typename properties from grants
      const cleanGrants = parsedGrants.map(grant => {
        const { __typename, ...cleanGrant } = grant;
        return cleanGrant;
      });

      // Create new result object with cleaned grants
      const result = {
        ...rawResult,
        grants: cleanGrants
      };

      console.log('[grantService] Final result with cleaned grants:', {
        success: result.success,
        userId: result.userId,
        grantsCount: result.grants.length,
        hasError: !!result.error
      });

      return result;
    } catch (error) {
      console.error('[grantService] Error getting applicable grants:', error);
      throw error;
    }
  }

  /**
   * Search grants with advanced filters
   */
  async searchGrants(clientId, searchCriteria) {
    try {
      const {
        searchTerm,
        targetType,
        status,
        targetGroup,
        targetGroupRole,
        targetOrgLevel,
        resources,
        sortBy = 'updatedAt',
        sortOrder = 'desc'
      } = searchCriteria;

      // Build filter
      const filter = {
        clientId: { eq: clientId }
      };

      if (searchTerm) {
        filter.or = [
          { grantName: { contains: searchTerm } },
          { description: { contains: searchTerm } }
        ];
      }

      if (targetType) filter.targetType = { eq: targetType };
      if (status) filter.status = { eq: status };
      if (targetGroup) filter.targetGroup = { eq: targetGroup };
      if (targetGroupRole) filter.targetGroupRole = { eq: targetGroupRole };
      if (targetOrgLevel) filter.targetOrgLevel = { eq: targetOrgLevel };

      // Get grants
      const grants = await this.listGrants(clientId, filter);

      // Filter by resources if specified
      let filtered = grants;
      if (resources && resources.length > 0) {
        filtered = grants.filter(grant =>
          grant.resources && grant.resources.some(r => resources.includes(r))
        );
      }

      // Sort results
      filtered.sort((a, b) => {
        const aVal = a[sortBy] || '';
        const bVal = b[sortBy] || '';

        if (sortOrder === 'asc') {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });

      return filtered;
    } catch (error) {
      console.error('Error searching grants:', error);
      throw new Error(`Failed to search grants: ${error.message}`);
    }
  }

  /**
   * Clone an existing grant
   */
  async cloneGrant(grantId, newName, clientId) {
    try {
      // Get original grant
      const original = await this.getGrant(grantId);

      if (!original) {
        throw new Error('Grant not found');
      }

      // Create new grant with modified properties
      const cloned = {
        ...original,
        id: undefined,
        grantName: newName,
        createdAt: undefined,
        updatedAt: undefined,
        _version: undefined,
        _deleted: undefined,
        _lastChangedAt: undefined
      };

      return await this.createGrant(cloned, clientId);
    } catch (error) {
      console.error('Error cloning grant:', error);
      throw new Error(`Failed to clone grant: ${error.message}`);
    }
  }

  /**
   * Get grant statistics for a client
   */
  async getGrantStatistics(clientId) {
    try {
      const grants = await this.listGrants(clientId);

      const stats = {
        total: grants.length,
        byStatus: {},
        byType: {},
        byPriority: {},
        resourceCoverage: new Set(),
        temporal: 0,
        conflicts: []
      };

      grants.forEach(grant => {
        // Count by status
        stats.byStatus[grant.status] = (stats.byStatus[grant.status] || 0) + 1;

        // Count by type
        stats.byType[grant.targetType] = (stats.byType[grant.targetType] || 0) + 1;

        // Count by priority
        const priority = grant.priority || 1;
        stats.byPriority[priority] = (stats.byPriority[priority] || 0) + 1;

        // Track resources
        if (grant.resources) {
          grant.resources.forEach(r => stats.resourceCoverage.add(r));
        }

        // Count temporal grants
        if (grant.effectiveFrom || grant.effectiveTo) {
          stats.temporal++;
        }
      });

      // Convert Set to array
      stats.resourceCoverage = Array.from(stats.resourceCoverage);

      // Detect potential conflicts (same target, overlapping resources)
      const targetMap = {};
      grants.forEach(grant => {
        const targetKey = grant.targetType === 'USER' ?
          `USER:${grant.targetUserId}` :
          `ROLE:${grant.targetGroup}:${grant.targetGroupRole}:${grant.targetOrgLevel}`;

        if (!targetMap[targetKey]) {
          targetMap[targetKey] = [];
        }
        targetMap[targetKey].push(grant);
      });

      Object.entries(targetMap).forEach(([target, targetGrants]) => {
        if (targetGrants.length > 1) {
          // Check for resource overlaps
          const resourceMap = {};
          targetGrants.forEach(grant => {
            if (grant.resources) {
              grant.resources.forEach(resource => {
                if (!resourceMap[resource]) {
                  resourceMap[resource] = [];
                }
                resourceMap[resource].push(grant);
              });
            }
          });

          Object.entries(resourceMap).forEach(([resource, resourceGrants]) => {
            if (resourceGrants.length > 1) {
              stats.conflicts.push({
                target,
                resource,
                grants: resourceGrants.map(g => ({
                  id: g.id,
                  name: g.grantName,
                  priority: g.priority
                }))
              });
            }
          });
        }
      });

      return stats;
    } catch (error) {
      console.error('Error getting grant statistics:', error);
      throw new Error(`Failed to get grant statistics: ${error.message}`);
    }
  }
}

export const grantService = new GrantService();